import { createApi } from '@reduxjs/toolkit/query/react';
import { createAppBaseQuery } from '../../services/baseQuery';
import { getAccessToken } from '../../services/authTokens';

export const dealApi = createApi({
  reducerPath: 'dealApi',
  baseQuery: createAppBaseQuery(),
  tagTypes: ['Deals', 'Deal', 'Merchants', 'Categories'],
  endpoints: (builder) => ({
    listDeals: builder.query({
      query: (params) => ({ url: '/deals', params }),
      providesTags: ['Deals'],
      serializeQueryArgs: ({ endpointName }) => endpointName,
      merge: (currentCache, newItems, { arg }) => {
        if (!arg.cursor) return newItems;
        return { ...currentCache, items: [...currentCache.items, ...newItems.items], page: newItems.page };
      },
      forceRefetch: ({ currentArg, previousArg }) => currentArg !== previousArg,
    }),
    getDeal: builder.query({
      query: (publicId) => `/deals/${publicId}`,
      providesTags: (result, error, publicId) => [{ type: 'Deal', id: publicId }],
    }),
    createDeal: builder.mutation({
      query: (body) => ({ url: '/deals', method: 'POST', body }),
      invalidatesTags: ['Deals'],
    }),
    updateDeal: builder.mutation({
      query: ({ publicId, ...body }) => ({ url: `/deals/${publicId}`, method: 'PATCH', body }),
      invalidatesTags: (result, error, { publicId }) => ['Deals', { type: 'Deal', id: publicId }],
    }),
    listMerchants: builder.query({
      query: () => '/search/merchants',
      providesTags: ['Merchants'],
    }),
    listCategories: builder.query({
      query: () => '/search/categories',
      providesTags: ['Categories'],
    }),
    vote: builder.mutation({
      query: ({ dealId, vote }) => ({ url: `/deals/${dealId}/vote`, method: 'PUT', body: { vote } }),
      invalidatesTags: (result, error, { dealId }) => ['Deals', { type: 'Deal', id: dealId }],
    }),
    removeVote: builder.mutation({
      query: (dealId) => ({ url: `/deals/${dealId}/vote`, method: 'DELETE' }),
      invalidatesTags: (result, error, dealId) => ['Deals', { type: 'Deal', id: dealId }],
    }),
    saveDeal: builder.mutation({
      query: (dealId) => ({ url: `/deals/${dealId}/save`, method: 'PUT' }),
      invalidatesTags: (result, error, dealId) => ['Deals', { type: 'Deal', id: dealId }],
    }),
    unsaveDeal: builder.mutation({
      query: (dealId) => ({ url: `/deals/${dealId}/save`, method: 'DELETE' }),
      invalidatesTags: (result, error, dealId) => ['Deals', { type: 'Deal', id: dealId }],
    }),
    getComments: builder.query({
      query: ({ dealId, cursor }) => ({ url: `/deals/${dealId}/comments`, params: { cursor } }),
    }),
    createComment: builder.mutation({
      query: ({ dealId, ...body }) => ({ url: `/deals/${dealId}/comments`, method: 'POST', body }),
      invalidatesTags: (result, error, { dealId }) => [{ type: 'Deal', id: dealId }],
    }),
    search: builder.query({
      query: (params) => ({ url: '/search', params }),
    }),
    getSuggestions: builder.query({
      query: (q) => `/search/suggestions?q=${encodeURIComponent(q)}`,
    }),
  }),
});

const IMAGE_BASE = '/api/v1';

export async function uploadImage(file) {
  const token = getAccessToken();
  const formData = new FormData();
  formData.append('file', file);

  const res = await fetch(`${IMAGE_BASE}/images/upload`, {
    method: 'POST',
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ title: 'Upload failed' }));
    const detail = err.title || err.message || 'Upload failed';
    const error = new Error(detail);
    error.code = err.code;
    error.status = res.status;
    throw error;
  }

  return res.json();
}

export const {
  useListDealsQuery,
  useGetDealQuery,
  useCreateDealMutation,
  useUpdateDealMutation,
  useListMerchantsQuery,
  useListCategoriesQuery,
  useVoteMutation,
  useRemoveVoteMutation,
  useSaveDealMutation,
  useUnsaveDealMutation,
  useGetCommentsQuery,
  useCreateCommentMutation,
  useSearchQuery,
  useGetSuggestionsQuery,
} = dealApi;
